import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
  Preview as PreviewIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { EmailMessage, EmailTemplate, EmailAttachment } from './types';
import emailService from './services/emailService';

const EmailComposePage: React.FC = () => {
  const [emailData, setEmailData] = useState<EmailMessage>({
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    text: '',
    html: '',
    attachments: [],
  });

  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [templateData, setTemplateData] = useState<Record<string, any>>({});
  
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState({ html: '', text: '' });
  
  const [attachments, setAttachments] = useState<EmailAttachment[]>([]);

  // Load templates on mount
  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    const loadedTemplates = await emailService.getEmailTemplates();
    setTemplates(loadedTemplates);
  };

  const handleInputChange = (field: keyof EmailMessage, value: any) => {
    setEmailData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setSuccessMessage('');
    setErrorMessage('');
  };

  const handleTemplateChange = async (templateId: string) => {
    setSelectedTemplate(templateId);
    if (!templateId) {
      setEmailData((prev) => ({
        ...prev,
        subject: '',
        html: '',
        text: '',
        templateId: undefined,
        templateData: undefined,
      }));
      return;
    }

    const template = await emailService.getEmailTemplate(templateId);
    if (template) {
      setEmailData((prev) => ({
        ...prev,
        subject: template.subject,
        templateId: templateId,
      }));
      
      // Initialize template data with empty values
      const initialData: Record<string, any> = {};
      template.variables.forEach((variable) => {
        initialData[variable] = '';
      });
      setTemplateData(initialData);
    }
  };

  const handleTemplateDataChange = (variable: string, value: any) => {
    setTemplateData((prev) => ({
      ...prev,
      [variable]: value,
    }));
  };

  const handleFileAttachment = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const newAttachments: EmailAttachment[] = [];
    
    Array.from(files).forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        newAttachments.push({
          filename: file.name,
          content: content,
          contentType: file.type,
          size: file.size,
        });
        
        if (newAttachments.length === files.length) {
          setAttachments((prev) => [...prev, ...newAttachments]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handlePreview = async () => {
    if (selectedTemplate && Object.keys(templateData).length > 0) {
      const rendered = await emailService.renderTemplate(selectedTemplate, templateData);
      if (rendered) {
        setPreviewContent(rendered);
        setPreviewOpen(true);
      }
    } else {
      setPreviewContent({
        html: emailData.html || '',
        text: emailData.text || '',
      });
      setPreviewOpen(true);
    }
  };

  const validateEmail = (): boolean => {
    if (!emailData.to || emailData.to.toString().trim() === '') {
      setErrorMessage('Recipient email address is required');
      return false;
    }

    const toEmails = typeof emailData.to === 'string' 
      ? emailData.to.split(',').map(e => e.trim())
      : emailData.to;

    for (const email of toEmails) {
      if (!emailService.validateEmailAddress(email)) {
        setErrorMessage(`Invalid email address: ${email}`);
        return false;
      }
    }

    if (!emailData.subject || emailData.subject.trim() === '') {
      setErrorMessage('Email subject is required');
      return false;
    }

    if (!emailData.text && !emailData.html && !selectedTemplate) {
      setErrorMessage('Email content is required');
      return false;
    }

    return true;
  };

  const handleSendEmail = async () => {
    if (!validateEmail()) {
      return;
    }

    setLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const message: EmailMessage = {
        ...emailData,
        attachments: attachments,
      };

      if (selectedTemplate) {
        message.templateId = selectedTemplate;
        message.templateData = templateData;
      }

      const result = await emailService.sendEmail(message);
      
      if (result.success) {
        setSuccessMessage(`Email sent successfully! Message ID: ${result.messageId || 'N/A'}`);
        // Reset form
        setEmailData({
          to: '',
          cc: '',
          bcc: '',
          subject: '',
          text: '',
          html: '',
          attachments: [],
        });
        setAttachments([]);
        setSelectedTemplate('');
        setTemplateData({});
      } else {
        setErrorMessage(result.error || 'Failed to send email');
      }
    } catch (error: any) {
      setErrorMessage(error.message || 'Failed to send email');
    } finally {
      setLoading(false);
    }
  };

  const selectedTemplateObj = templates.find((t) => t.id === selectedTemplate);

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3} sx={{ p: 4, maxWidth: 900, mx: 'auto' }}>
        <Typography variant="h4" gutterBottom>
          Compose Email
        </Typography>

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
            {successMessage}
          </Alert>
        )}

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage('')}>
            {errorMessage}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Template Selection */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Email Template (Optional)</InputLabel>
              <Select
                value={selectedTemplate}
                label="Email Template (Optional)"
                onChange={(e) => handleTemplateChange(e.target.value)}
              >
                <MenuItem value="">
                  <em>None - Compose manually</em>
                </MenuItem>
                {templates.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Template Variables */}
          {selectedTemplateObj && selectedTemplateObj.variables.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Template Variables
              </Typography>
              <Grid container spacing={2}>
                {selectedTemplateObj.variables.map((variable) => (
                  <Grid item xs={12} sm={6} key={variable}>
                    <TextField
                      fullWidth
                      label={variable}
                      value={templateData[variable] || ''}
                      onChange={(e) => handleTemplateDataChange(variable, e.target.value)}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}

          {/* To */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="To"
              value={emailData.to}
              onChange={(e) => handleInputChange('to', e.target.value)}
              helperText="Separate multiple emails with commas"
              required
            />
          </Grid>

          {/* CC */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="CC"
              value={emailData.cc}
              onChange={(e) => handleInputChange('cc', e.target.value)}
              helperText="Optional"
            />
          </Grid>

          {/* BCC */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="BCC"
              value={emailData.bcc}
              onChange={(e) => handleInputChange('bcc', e.target.value)}
              helperText="Optional"
            />
          </Grid>

          {/* Subject */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Subject"
              value={emailData.subject}
              onChange={(e) => handleInputChange('subject', e.target.value)}
              required
              disabled={!!selectedTemplate}
            />
          </Grid>

          {/* Email Content - Only show if no template selected */}
          {!selectedTemplate && (
            <>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Plain Text Content"
                  value={emailData.text}
                  onChange={(e) => handleInputChange('text', e.target.value)}
                  multiline
                  rows={6}
                  helperText="Plain text version of the email"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="HTML Content"
                  value={emailData.html}
                  onChange={(e) => handleInputChange('html', e.target.value)}
                  multiline
                  rows={8}
                  helperText="HTML version of the email (optional)"
                />
              </Grid>
            </>
          )}

          {/* Attachments */}
          <Grid item xs={12}>
            <Box>
              <Button
                variant="outlined"
                component="label"
                startIcon={<AttachFileIcon />}
              >
                Add Attachments
                <input
                  type="file"
                  hidden
                  multiple
                  onChange={handleFileAttachment}
                />
              </Button>
              {attachments.length > 0 && (
                <List dense sx={{ mt: 1 }}>
                  {attachments.map((attachment, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={attachment.filename}
                        secondary={`${(attachment.size || 0 / 1024).toFixed(2)} KB`}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => handleRemoveAttachment(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<PreviewIcon />}
                onClick={handlePreview}
              >
                Preview
              </Button>
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                onClick={handleSendEmail}
                disabled={loading}
              >
                Send Email
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Email Preview
          <IconButton
            onClick={() => setPreviewOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="h6" gutterBottom>
            Subject: {emailData.subject}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            To: {emailData.to}
          </Typography>
          
          {previewContent.html && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                HTML Content:
              </Typography>
              <Box
                sx={{ 
                  border: 1, 
                  borderColor: 'divider', 
                  p: 2, 
                  borderRadius: 1,
                  backgroundColor: '#f5f5f5'
                }}
                dangerouslySetInnerHTML={{ __html: previewContent.html }}
              />
            </Box>
          )}
          
          {previewContent.text && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Plain Text Content:
              </Typography>
              <Box
                sx={{ 
                  border: 1, 
                  borderColor: 'divider', 
                  p: 2, 
                  borderRadius: 1,
                  whiteSpace: 'pre-wrap',
                  backgroundColor: '#f5f5f5'
                }}
              >
                {previewContent.text}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailComposePage;
